// ==============================
// FILE: src/components/AccountManagers.js
// ==============================
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { auth, db } from "../firebase";
import { useFreeLimits } from "../hooks/useFreeLimits";
import UpgradeModal from "./UpgradeModal";
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { FiEye } from "react-icons/fi";
import { FaYoutube } from "react-icons/fa";
import PageScaffold from "./ui/PageScaffold";
import "../styles/layout.css";
import VideoLightbox from "./ui/VideoLightbox";
import {
  PAGE_ACCESS,
  DEFAULT_MANAGER_MENU_KEYS,
  EXTRA_MANAGER_PAGES,
  buildNoneAccessMap,
  applyManagerDefaults,
  normalizePageAccess,
} from "../access/pageAccess";

/* ------------------------------ helpers ------------------------------ */
function fmtDate(d) {
  try {
    const x = typeof d === "string" ? new Date(d) : d?.toDate ? d.toDate() : new Date(d);
    return isNaN(x.getTime()) ? "—" : x.toLocaleString();
  } catch {
    return "—";
  }
}
const tryParseJSON = (t) => { try { return JSON.parse(t); } catch { return null; } };
async function fetchJSON(url, options) {
  const r = await fetch(url, options);
  const txt = await r.text();
  const j = tryParseJSON(txt) || {};
  return { ok: r.ok, body: j, raw: txt, status: r.status };
}


/* --------------------------------- view ---------------------------------- */
export default function AccountManagers({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [showForm, setShowForm] = useState(false);
  const { free } = useFreeLimits(user);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [editing, setEditing] = useState(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    sendOtac: true,
    passwordHint: "",
    delivery: "upon-death",  // "upon-death" | "immediate" | "scheduled"
    deliverAt: "",
  });
  const firstFieldRef = useRef(null);

  const [profileName, setProfileName] = useState("");
  const [showVideo, setShowVideo] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false); // ✅ enabled drawer

  // Access toggle state
  const [accessEnabled, setAccessEnabled] = useState(false);
  const [accessBusy, setAccessBusy] = useState(false);

  // Page-level access (NEW)
  // Account Managers are ALWAYS read-only + permissioned via pageAccess (limited by default)
  const [accessMode, setAccessMode] = useState("limited"); // kept for backward compatibility
  const [pageAccess, setPageAccess] = useState(() => applyManagerDefaults(buildNoneAccessMap()));
  const [permBusy, setPermBusy] = useState(false);

  /* ------------------------------ load list ------------------------------ */
  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const u = auth.currentUser;
        const tok = u ? await u.getIdToken() : "";
        const { ok, body, raw } = await fetchJSON("/api/accountManagers/listMine", {
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (!ok) throw new Error(body.error || raw || "Failed to load managers");
        setItems(Array.isArray(body.items) ? body.items : []);
      } catch (e) {
        setErr(e.message || "Failed to load account managers");
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.uid]);

  /* ------------------------------ owner name ----------------------------- */
  useEffect(() => {
    if (!user?.uid) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const d = snap.exists() ? (snap.data() || {}) : {};
        const n =
          d.name ||
          d.fullName ||
          d.displayName ||
          [d.firstName, d.lastName].filter(Boolean).join(" ") ||
          user.displayName ||
          user.email ||
          "";
        setProfileName(String(n || "").trim());
      } catch {}
    })();
  }, [user?.uid]);

  const ownerNameForMsg =
    (typeof profileName === "string" && profileName.trim()) ||
    (user && (user.displayName || user.name || user.email)) ||
    "";

  /* --------------------------- default message --------------------------- */
  useEffect(() => {
    setForm((f) => {
      if (f.message && f.message.trim()) return f;
      return {
        ...f,
        message:
          `Hi ${((f.name || "").trim() || "there")}, ` +
          (
            ownerNameForMsg
              ? `you’ve been invited to become an Account Manager for “${ownerNameForMsg}”. `
              : `you’ve been invited to become an Account Manager. `
          ) +
          `You’ll have read-only access to help carry out final wishes with care, privacy, and respect.`,
      };
    });
  }, [ownerNameForMsg]);

  /* ------------------------------- modal ops ----------------------------- */
  function setField(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function closeForm() { setShowForm(false); setEditing(null); }

  async function reloadList() {
    try {
      const u = auth.currentUser;
      const tok = u ? await u.getIdToken() : "";
      const { ok, body } = await fetchJSON("/api/accountManagers/listMine", {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (ok) setItems(Array.isArray(body.items) ? body.items : []);
    } catch {}
  }

  // Persist access across API + Firestore AND mirror into invite doc
  async function applyAccess(inviteId, next, currentEditing) {
    const u = auth.currentUser;
    const tok = u ? await u.getIdToken() : "";

    async function mirrorInviteAccess() {
      try {
        await setDoc(
          doc(db, "accountManagers", inviteId),
          { accessEnabled: !!next, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch {}
    }

    // 1) Preferred API
    const r1 = await fetchJSON("/api/accountManagers/setAccess", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ id: inviteId, accessEnabled: !!next }),
    });
    if (r1.ok && !r1.body.error) {
      await mirrorInviteAccess();
      return true;
    }

    // 2) Legacy allow/deny
    const r2 = await fetchJSON(next ? "/api/accountManagers/allow" : "/api/accountManagers/deny", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ id: inviteId }),
    });
    if (r2.ok && !r2.body.error) {
      await mirrorInviteAccess();
      return true;
    }

    // 3) Firestore fallback (no email)
    try {
      const ownerUid = user?.uid;
      const mUid =
        currentEditing?.managerUid ||
        currentEditing?.uid ||
        currentEditing?.userUid ||
        currentEditing?.userId ||
        currentEditing?.managerUserId ||
        currentEditing?.acceptedBy ||
        "";

      if (ownerUid && mUid) {
        await setDoc(
          doc(db, "accounts", ownerUid, "managers", mUid),
          { accessEnabled: !!next, updatedAt: serverTimestamp() },
          { merge: true }
        );
        // Ensure managers are limited + defaults applied when enabling access
        await setDoc(
          doc(db, "grants", `${ownerUid}_${mUid}`),
          {
            ownerUid,
            managerUid: mUid,
            active: !!next,
            readOnly: true,
            accessMode: "limited",
            pageAccess: applyManagerDefaults(normalizePageAccess(currentEditing?.pageAccess || {})),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
      await mirrorInviteAccess();
      return true;
    } catch {
      return false;
    }
  }

  // Persist page-level permissions across API + Firestore.
  async function applyPermissions(inviteId, nextMode, nextPageAccess, currentEditing) {
    const u = auth.currentUser;
    const tok = u ? await u.getIdToken() : "";

    // Managers are always in limited mode.
    const mode = "limited";
    const cleanedPageAccess = applyManagerDefaults(normalizePageAccess(nextPageAccess));

    const mirrorInvite = async () => {
      try {
        await setDoc(
          doc(db, "accountManagers", inviteId),
          { accessMode: mode, pageAccess: cleanedPageAccess, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch {}
    };

    // Preferred API (owner-only)
    const r1 = await fetchJSON("/api/accountManagers/setPermissions", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
      body: JSON.stringify({ id: inviteId, accessMode: mode, pageAccess: cleanedPageAccess }),
    });
    if (r1.ok && !r1.body?.error) {
      await mirrorInvite();
      return true;
    }

    // Firestore fallback
    try {
      const ownerUid = user?.uid;
      const mUid =
        currentEditing?.managerUid ||
        currentEditing?.uid ||
        currentEditing?.userUid ||
        currentEditing?.userId ||
        currentEditing?.managerUserId ||
        currentEditing?.acceptedBy ||
        "";

      // Always store on invite (so future acceptance can copy it)
      await mirrorInvite();

      // Only store on grant if the manager is known/accepted
      if (ownerUid && mUid) {
        await setDoc(
          doc(db, "grants", `${ownerUid}_${mUid}`),
          { ownerUid, managerUid: mUid, accessMode: mode, pageAccess: cleanedPageAccess, updatedAt: serverTimestamp() },
          { merge: true }
        );
      }
      return true;
    } catch {
      return false;
    }
  }

  async function openForm(item = null) {
    setErr("");
    if (item) {
      setEditing(item);
      setForm((f) => ({
        ...f,
        name: item.name ?? item.recipient ?? f.name ?? "",
        email: item.email ?? f.email ?? "",
        message: (item.message && String(item.message)) || f.message || "",
        sendOtac: true,
        passwordHint: item.secretNote || item.passwordHint || "",
        delivery:
          item.delivery === "immediate" || item.delivery === "scheduled" || item.delivery === "upon-death"
            ? item.delivery
            : item.delivery === "upon_death"
            ? "upon-death"
            : "upon-death",
        deliverAt:
          item.delivery === "scheduled"
            ? item.deliverAt?.seconds
              ? new Date(item.deliverAt.seconds * 1000).toISOString().slice(0, 16)
              : item.deliverAt || ""
            : "",
      }));
      setAccessEnabled(!!item.accessEnabled);
      // Defaults: full access unless grant says otherwise
      setAccessMode("limited");
      setPageAccess(
        item.pageAccess && typeof item.pageAccess === "object"
          ? applyManagerDefaults(normalizePageAccess(item.pageAccess))
          : applyManagerDefaults(buildNoneAccessMap())
      );

      // read authoritative grant on open
      try {
        const ownerUid = auth.currentUser && auth.currentUser.uid;
        const managerUid =
          item.managerUid || item.uid || item.userUid || item.userId || item.managerUserId || item.acceptedBy;
        if (ownerUid && managerUid) {
          const gref = doc(db, "grants", `${ownerUid}_${managerUid}`);
          const gsnap = await getDoc(gref);
          if (gsnap.exists()) {
            const g = gsnap.data() || {};
            setAccessEnabled(!!g.active);
            setAccessMode("limited");
            const pa = (g.pageAccess && typeof g.pageAccess === "object") ? g.pageAccess : {};
            setPageAccess(applyManagerDefaults(normalizePageAccess(pa)));
          }
        }
      } catch {}
    } else {
      setEditing(null);
      setForm((f) => ({
        name: "",
        email: "",
        message: f.message || "",
        sendOtac: true,
        passwordHint: "",
        delivery: "upon-death",
        deliverAt: "",
      }));
      setAccessEnabled(false);
      setAccessMode("limited");
      setPageAccess(applyManagerDefaults(buildNoneAccessMap()));
    }
    if (free && items.length >= 1) {
      setShowUpgrade(true);
      return;
    }
    setShowForm(true);
    setTimeout(() => firstFieldRef.current?.focus(), 0);
  }

  /* --------------------------------- save -------------------------------- */
  async function handleSubmit(e) {
    e.preventDefault();
    setErr("");
    try {
      const u = auth.currentUser;
      const tok = u ? await u.getIdToken() : "";

      const includeSecret = !!(form.passwordHint && form.passwordHint.trim());
      const secretNote = includeSecret ? form.passwordHint.trim() : "";

      const isScheduled = form.delivery === "scheduled";
      let deliverAtIso = "";
      if (isScheduled) {
        if (!form.deliverAt) throw new Error("Please choose a date & time.");
        const d = new Date(form.deliverAt);
        if (isNaN(d.getTime())) throw new Error("Please choose a valid date & time.");
        if (d.getTime() < Date.now() + 60 * 1000)
          throw new Error("Scheduled time must be at least 1 minute in the future.");
        deliverAtIso = d.toISOString();
      }

      const appOrigin = window.location.origin;
      const verifyUrl = `${appOrigin}/accept-invitation`;
      const verifyAliases = [`${appOrigin}/am/accept`, `${appOrigin}/invite`];

      const creating = !editing?.id;
      const basePayload = {
        id: editing?.id || "",
        name: form.name,
        email: form.email,
        message: form.message,
        includeSecret,
        secretNote,
        otacDays: 7,
        delivery: form.delivery,
        deliverAt: isScheduled ? deliverAtIso : "",
        verifyUrl,
        verifyAliases,
        accountContext: "personal",
      };

      if (creating) {
        const createRes = await fetchJSON("/api/accountManagers/upsert", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
          body: JSON.stringify({ ...basePayload, sendOtac: !!form.sendOtac }),
        });
        if (!createRes.ok) throw new Error(createRes.body.error || createRes.raw || "Save failed");
      } else {
        // Prefer no-email updates; fallback to upsert if needed
        const preferred = await fetchJSON("/api/accountManagers/updateNoEmail", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
          body: JSON.stringify({ ...basePayload }),
        });
        if (!preferred.ok) {
          const fallback = await fetchJSON("/api/accountManagers/upsert", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
            body: JSON.stringify({ ...basePayload, sendOtac: false, suppressEmail: true, updateOnly: true }),
          });
          if (!fallback.ok) throw new Error(fallback.body.error || fallback.raw || "Save failed");
        }
        // Ensure toggle persisted too
        await applyAccess(editing.id, accessEnabled, editing);
      }

      await reloadList();
      closeForm();
    } catch (e2) {
      const errMsg = e2.message || "Save failed";
      if (errMsg.toLowerCase().includes("already an account manager")) {
        alert(errMsg);
      } else {
        setErr(errMsg);
      }
    }
  }

  /* ----------------------------- access toggle --------------------------- */
  async function handleAccessToggle(next) {
    if (!editing?.id) return;
    setAccessBusy(true);
    setErr("");
    try {
      const ok = await applyAccess(editing.id, next, editing);
      if (!ok) throw new Error("Failed to update access");
      setAccessEnabled(!!next);
      setItems((xs) => xs.map((x) => (x.id === editing.id ? { ...x, accessEnabled: !!next } : x)));
      setEditing((e) => (e ? { ...e, accessEnabled: !!next } : e));
    } catch (e) {
      setErr(e.message || "Failed to update access");
    } finally {
      setAccessBusy(false);
    }
  }

  // Managers are always in limited (permissioned) mode.
  // We only allow toggling of EXTRA pages; default pages are always on.

  function handlePageToggle(key, checked) {
    setPageAccess((m) => applyManagerDefaults({ ...m, [key]: !!checked }));
  }

  async function handleSavePermissions() {
    if (!editing?.id) return;
    setPermBusy(true);
    setErr("");
    try {
      const ok = await applyPermissions(editing.id, accessMode, pageAccess, editing);
      if (!ok) throw new Error("Failed to save permissions");
      setItems((xs) => xs.map((x) => (x.id === editing.id ? { ...x, accessMode, pageAccess } : x)));
      setEditing((e) => (e ? { ...e, accessMode, pageAccess } : e));
    } catch (e) {
      setErr(e.message || "Failed to save permissions");
    } finally {
      setPermBusy(false);
    }
  }

  /* --------------------------- send login link --------------------------- */
  async function handleSendLoginLink() {
    if (!editing?.id) return;
    setErr("");
    try {
      const u = auth.currentUser;
      const tok = u ? await u.getIdToken() : "";
      const r = await fetchJSON("/api/accountManagers/sendLoginLink", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ id: editing.id }),
      });
      if (r.ok) { alert("Login link sent."); return; }
      const url = `${window.location.origin}/manager-select`;
      await navigator.clipboard.writeText(url);
      alert("Login link copied to clipboard:\n" + url);
    } catch (e) {
      setErr(e.message || "Failed to send link");
    }
  }

  /* ------------------------------- delete -------------------------------- */
  async function handleDelete(item) {
    if (!item?.id) return;
    if (!window.confirm("Delete this account manager invitation?")) return;
    setErr("");
    try {
      const u = auth.currentUser;
      const tok = u ? await u.getIdToken() : "";

      // Try primary API
      const r = await fetchJSON("/api/accountManagers/delete", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ id: item.id }),
      });

      if (!r.ok || r.body?.error) {
        // Fallback: delete invite + links in Firestore
        const ownerUid = user?.uid;
        const mUid =
          item.managerUid ||
          item.uid ||
          item.userUid ||
          item.userId ||
          item.managerUserId ||
          item.acceptedBy ||
          "";

        try { await deleteDoc(doc(db, "accountManagers", item.id)); } catch {}
        if (ownerUid && mUid) {
          try { await deleteDoc(doc(db, "accounts", ownerUid, "managers", mUid)); } catch {}
          try { await deleteDoc(doc(db, "grants", `${ownerUid}_${mUid}`)); } catch {}
        }
      }

      await reloadList();
    } catch (e) {
      setErr(e.message || "Delete failed");
    }
  }

  /* ----------------------------- page actions ---------------------------- */
  const actionsDesktop = (
    <>
      <button className="btn am-help read" onClick={() => setShowDrawer(true)}>
        <FiEye className="am-help-ico" />
        Read how it works
      </button>
      <button className="btn am-help see" onClick={() => setShowVideo(true)}>
        <FaYoutube className="am-help-ico" />
        See how it works
      </button>
    </>
  );
  const actionsMobile = (
    <div className="am-help-row">
      <button className="btn am-help read" onClick={() => setShowDrawer(true)}>
        <FiEye className="am-help-ico" />
        Read how it works
      </button>
      <button className="btn am-help see" onClick={() => setShowVideo(true)}>
        <FaYoutube className="am-help-ico" />
        See how it works
      </button>
    </div>
  );

  /* -------------------------------- render ------------------------------- */
  return (
    <>
      <PageScaffold
        title="Account Managers"
        onAdd={() => openForm(null)}
        heroSrc="/lastwish-account-managers.jpg"
        heroAlt="Account Managers"
        actionsDesktop={actionsDesktop}
        actionsMobile={actionsMobile}
      >
        <div className="am-list">
          {err && (
            <div className="empty-state" style={{ color: "#9b1c1c" }}>
              {err}
            </div>
          )}
          {loading ? (
            <div className="empty-state">Loading…</div>
          ) : items.length === 0 ? (
            <div className="empty-state">No account managers yet.</div>
          ) : (
            items.map((it) => (
              <div key={it.id} className="card am-item">
                <div className="tile am-item-main">
                  <div
                    className="tile-thumb"
                    style={{ display: "grid", placeItems: "center", background: "#003353", borderRadius: "50%", color: "#fff", fontWeight: 700, fontSize: "1rem", width: 44, height: 44, flexShrink: 0 }}
                  >
                    {(it.name || it.recipient || it.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="tile-main">
                    <div className="tile-title">{it.name || it.recipient || "—"}</div>
                    <div className="tile-sub">{it.email || "—"}</div>
                    <div className="tile-sub" style={{ marginTop: 4 }}>
                      <span style={{ opacity: 0.85 }}>
                        {it.delivery === "immediate"
                          ? "Sends immediately"
                          : it.delivery === "scheduled"
                          ? `Scheduled: ${fmtDate(it.deliverAt)}`
                          : "Deliver upon death"}
                      </span>
                      {" · "}
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "2px 8px",
                          borderRadius: 999,
                          border: "1px solid #e5e7eb",
                          background: it.acceptedAt ? "#ecfdf5" : "#f9fafb",
                          color: it.acceptedAt ? "#065f46" : "#374151",
                          marginLeft: 4,
                          fontSize: 12,
                        }}
                      >
                        {it.acceptedAt ? "Accepted" : "Pending"}
                      </span>
                      {typeof it.accessEnabled === "boolean" && (
                        <>
                          {" · "}
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "2px 8px",
                              borderRadius: 999,
                              border: "1px solid #e5e7eb",
                              background: it.accessEnabled ? "#ecfdf5" : "#f3f4f6",
                              color: it.accessEnabled ? "#065f46" : "#374151",
                              marginLeft: 4,
                              fontSize: 12,
                            }}
                          >
                            {it.accessEnabled ? "Access enabled" : "Access disabled"}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="am-item-actions">
                  <button className="btn" style={{ background: "#f15822", color: "#fff", border: "none" }} onClick={() => openForm(it)}>Edit</button>
                  <button className="btn btn-danger" onClick={() => handleDelete(it)}>Delete</button>
                </div>
              </div>
            ))
          )}
        </div>
      </PageScaffold>

      {/* Modal: site-wide style preserved */}
      {showForm && (
        <div className="modal-bg" onClick={closeForm}>
          <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
            <h3>{editing ? "Edit Account Manager" : "Add Account Manager"}</h3>
            <div className="modal-body">
              <label className="label">Name</label>
              <input
                ref={firstFieldRef}
                className="input"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                required
                placeholder="Full name"
              />

              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                required
                placeholder="name@example.com"
              />

              <label className="label">Message</label>
              <textarea
                className="input"
                rows={6}
                value={form.message}
                onChange={(e) => setField("message", e.target.value)}
                placeholder="Personal note to include with the invitation"
              />

              <div style={{ marginTop: 10 }}>
                <label
                  className="label"
                  style={{ display: "inline-flex", alignItems: "center", gap: 10, whiteSpace: "nowrap" }}
                  title="Include a single-use code in the email (recommended)"
                >
                  <input
                    type="checkbox"
                    checked={form.sendOtac}
                    onChange={(e) => setField("sendOtac", e.target.checked)}
                  />
                  Send a one-time access code (recommended)
                </label>
                <div style={{ color: "#6b7280", fontSize: 13, marginTop: 6 }}>
                  A single-use code is generated and included in the email. The recipient must enter it after clicking “Accept Invitation.”
                </div>
              </div>

              <label className="label" style={{ marginTop: 12 }}>Password hint (optional)</label>
              <textarea
                className="input"
                rows={3}
                value={form.passwordHint}
                onChange={(e) => setField("passwordHint", e.target.value)}
                placeholder="If you want to provide a hint for your master password, enter it here."
              />

              <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
                <label className="label">Delivery</label>
                <select className="input" value={form.delivery} onChange={(e) => setField("delivery", e.target.value)}>
                  <option value="upon-death">Upon death</option>
                  <option value="immediate">Send immediately</option>
                  <option value="scheduled">Schedule a date & time</option>
                </select>

                {form.delivery === "scheduled" && (
                  <>
                    <label className="label">Deliver at</label>
                    <input
                      type="datetime-local"
                      className="input"
                      value={form.deliverAt}
                      onChange={(e) => setField("deliverAt", e.target.value)}
                      required
                      min={new Date(Date.now() + 2 * 60 * 1000).toISOString().slice(0, 16)}
                    />
                  </>
                )}
              </div>

              {/* Access toggle */}
              <div style={{ marginTop: 14, padding: 12, border: "1px solid #eee", borderRadius: 8, background: "#fafafa" }}>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                  title={editing?.acceptedAt ? "Enable read-only access now." : "Available after the invitation is accepted."}
                >
                  <input
                    type="checkbox"
                    checked={accessEnabled}
                    onChange={(e) => handleAccessToggle(e.target.checked)}
                    disabled={!editing?.acceptedAt || accessBusy}
                  />
                  <span><strong>Allow user access to the account now</strong></span>
                </label>
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>
                  {editing?.acceptedAt
                    ? "Toggle to immediately enable/disable this manager’s read-only access (no email re-sent)."
                    : "This becomes available after the invite is accepted."}
                </div>
                <div style={{ marginTop: 10 }}>
                  <button type="button" className="btn" onClick={handleSendLoginLink} disabled={!editing?.acceptedAt}>
                    Send login link
                  </button>
                </div>
              </div>

              {/* Page-level permissions */}
              <div style={{ marginTop: 14, padding: 12, border: "1px solid #eee", borderRadius: 8, background: "#fafafa" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 900, color: "#2a0516" }}>Page access</div>
                  <button
                    type="button"
                    className="btn"
                    onClick={handleSavePermissions}
                    disabled={!editing?.id || permBusy}
                    title={editing?.acceptedAt ? "Save permissions" : "You can save these settings now; they will apply once the invite is accepted."}
                  >
                    {permBusy ? "Saving…" : "Save permissions"}
                  </button>
                </div>

                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 8, lineHeight: 1.45 }}>
                  Account Managers are <b>read-only</b>. When access is enabled, they automatically see a small default set of pages.
                  You can grant <b>extra pages</b> below (including <b>Home</b>, which is blocked by default).
                </div>

                <div style={{ marginTop: 12, border: "1px solid #f0f0f0", borderRadius: 10, padding: 10, background: "#fff" }}>
                  <div style={{ fontWeight: 900, marginBottom: 8 }}>Default access (always on)</div>
                  <ul style={{ margin: 0, paddingLeft: 18, color: "#374151" }}>
                    {DEFAULT_MANAGER_MENU_KEYS.map((k) => {
                      const p = PAGE_ACCESS.find((x) => x.key === k);
                      return <li key={k}>{p?.label || k}</li>;
                    })}
                  </ul>
                </div>

                <div style={{ marginTop: 12, border: "1px solid #f0f0f0", borderRadius: 10, padding: 10, background: "#fff" }}>
                  <div style={{ fontWeight: 900, marginBottom: 8 }}>Optional extra pages</div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {EXTRA_MANAGER_PAGES.map((p) => (
                      <label key={p.key} style={{ display: "grid", gridTemplateColumns: "22px 1fr", gap: 10, alignItems: "start" }}>
                        <input
                          type="checkbox"
                          style={{ marginTop: 2 }}
                          checked={pageAccess[p.key] === true}
                          onChange={(e) => handlePageToggle(p.key, e.target.checked)}
                          disabled={permBusy}
                        />
                        <div>
                          <div style={{ fontWeight: 800, color: "#111827" }}>{p.label}</div>
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{p.description || ""}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn btn-main" type="submit">{editing ? "Update" : "Save"}</button>
              <button className="btn btn-cancel" type="button" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ✅ Read how it works drawer */}
      {showDrawer && (
        <div className="dp-drawer-bg" onClick={() => setShowDrawer(false)}>
          <aside className="dp-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="dp-drawer-head">
              <div className="dp-drawer-title">How Account Managers work</div>
              <div className="dp-drawer-tools">
                <button className="btn btn-main" onClick={() => window.print()} type="button">Print</button>
                <button className="btn btn-cancel" onClick={() => setShowDrawer(false)} type="button">Close</button>
              </div>
            </div>
            <div className="dp-drawer-body">
              <ol>
                <li><b>Invite:</b> Add a trusted person’s name & email, include an optional password hint, and send the invite.</li>
                <li><b>Accept:</b> They open the invite link, enter the one-time code (OTAC) and create a manager login.</li>
                <li><b>Enable Access:</b> After acceptance, toggle <i>“Allow user access to the account now”</i>. This writes a <code>grants</code> record to allow <b>read-only</b> access.</li>
                <li><b>Manager Login:</b> They use <i>Manager Access</i> to view your dashboard. UI editing is disabled and Security Rules block writes.</li>
                <li><b>Revoke:</b> Toggle access off anytime. The grant is disabled instantly.</li>
              </ol>
              <p><b>Tip:</b> If a manager can’t see your data, check the invitation shows <i>Accepted</i> and that <i>Access enabled</i> is on.</p>
            </div>
          </aside>
        </div>
      )}

      <VideoLightbox
        open={showVideo}
        onClose={() => setShowVideo(false)}
        url="https://youtu.be/bk96CH5Kgfs?si=IJ2NQStfdfB4Bxqg"
        title="How it works (video)"
        startAt={0}
        captions={false}
        startMuted={false}
      />

      <style>{`
        .am-help-row { display: flex; gap: 10px; flex-wrap: wrap; }
        .am-help { border-radius: 8px; padding: 10px 14px; display: inline-flex; align-items: center; gap: 10px; font-weight: 700; color: #fff; border: none; }
        .am-help.read { background: #650202; color: #fff; }
        .am-help.see  { background: #003353; color: #fff; }
        .am-help-ico { font-size: 1.1rem; }
        .am-list { display: grid; grid-template-columns: 1fr; gap: 16px; }
        .am-item { display: flex; align-items: center; gap: 12px; padding: 12px; }
        .am-item-main { flex: 1; display: flex; align-items: center; gap: 12px; }
        .am-item-actions { display: flex; gap: 8px; }
        @media (max-width: 700px) { .am-item { flex-direction: column; align-items: stretch; } .am-item-actions { justify-content: flex-end; } }

        /* Drawer (matches other pages) */
        .dp-drawer-bg{position:fixed;inset:0;background:rgba(0,0,0,0.3);z-index:10000;display:flex;justify-content:flex-end}
        .dp-drawer{position:fixed;top:56px;right:0;height:calc(100vh - 56px);width:min(520px,95vw);background:#fff;box-shadow:-8px 0 24px rgba(0,0,0,0.15);border-top-left-radius:12px;border-bottom-left-radius:12px;display:flex;flex-direction:column;overflow:hidden}
        .dp-drawer-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:14px 16px;border-bottom:1px solid #f0f0f0;background:#fafafa}
        .dp-drawer-title{font-weight:700;color:#2a0516}
        .dp-drawer-tools{display:flex;gap:8px}
        .dp-drawer-body{padding:16px;overflow:auto}
      `}</style>
      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} feature="accountManagers" />
    </>
  );
}
